import React from 'react';
import { Confirm } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { ENTITY_ACCESS_TYPE, RESOURCE_TYPE } from '../../HelperMethods/role-resource-constants';
import ShouldComponentRender from './ShouldComponentRender';
import { isTaskParent } from '../../HelperMethods/TaskUtilities';

export default class ActionsColumnRenderer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      confirmModal: {
        content: '',
        header: '',
        cancelButton: 'Cancel',
        confirmButton: 'OK',
        onCancel: () => {},
        onConfirm: () => {},
        open: false,
      },
    };
    this.openConfirmModal = this.openConfirmModal.bind(this);
    this.closeConfirmModal = this.closeConfirmModal.bind(this);
  }

  openConfirmModal(type) {
    const { context, data, rowIndex } = this.props;
    let header,
      content,
      confirmButton = 'OK';
    if (type === 'edit') {
      // Prevent default behavior for edit if the parent component handles the modal
      if (context.disableDefaultmodal) {
        if (context.editItem) {
          context.editItem(data, rowIndex); // Call the parent-provided edit logic
        }
        return; // Skip the default behavior
      }
    } else if (type === 'delete') {
      //  delete uses shared warning + deleteMessage
      header = 'Warning';
      content = context.deleteMessage || 'Are you sure you want to delete this item?';
    }
    this.setState({
      confirmModal: {
        open: true,
        header,
        content,
        cancelButton: 'Cancel',
        confirmButton,
        onCancel: () => {
          this.closeConfirmModal();
        },
        onConfirm: () => {
          if (type === 'delete') {
            context.deleteItem(data, rowIndex);
          }
          this.closeConfirmModal();
        },
      },
    });
  }

  closeConfirmModal() {
    this.setState({
      confirmModal: {
        open: false,
        header: '',
        content: '',
        cancelButton: 'Cancel',
        confirmButton: 'OK',
        onCancel: () => {},
        onConfirm: () => {},
      },
    });
  }

  render() {
    const {
      context: {
        editItem,
        deleteItem,
        duplicateItem,
        disableDeleteConfirmationModal,
        disableEditForActive,
        resource,
        filterParentTask = true,
      },
      data,
      rowIndex,
    } = this.props;
    const { confirmModal } = this.state;
    if (data?.showActionRenderer === false) {
      return null;
    }
    return (
      <div style={{ display: 'flex', gap: '6px' }}>
        <UpdateButtonComponent
          rowIndex={rowIndex}
          data={data}
          editItem={editItem}
          disableEditForActive={disableEditForActive}
          openConfirmModal={this.openConfirmModal}
          resource={resource}
        />
        <DuplicateButtonComponent
          rowIndex={rowIndex}
          data={data}
          duplicateItem={duplicateItem}
          openConfirmModal={this.openConfirmModal}
          resource={resource}
        />
        <DeleteButtonComponent
          rowIndex={rowIndex}
          data={data}
          deleteItem={deleteItem}
          resource={resource}
          disableDeleteConfirmationModal={disableDeleteConfirmationModal}
          openConfirmModal={this.openConfirmModal}
          filterParentTask={filterParentTask}
        />
        {confirmModal.open && (
          <Confirm
            size="mini"
            open={confirmModal.open}
            header={confirmModal.header}
            content={confirmModal.content}
            cancelButton={confirmModal.cancelButton}
            confirmButton={confirmModal.confirmButton}
            onCancel={confirmModal.onCancel}
            onConfirm={confirmModal.onConfirm}
          />
        )}
      </div>
    );
  }
}

function UpdateButtonComponent({ editItem, resource, data, rowIndex, disableEditForActive, openConfirmModal }) {
  if (!editItem) return null;
  const isDisabled = disableEditForActive && data.isActive;
  const updateButton = (
    <button
      type="button"
      onClick={() => {
        data.isActive ? openConfirmModal('edit') : editItem(data, rowIndex);
      }}
      disabled={isDisabled}
      title={isDisabled ? 'Cannot edit active item' : 'Edit'}
      className={`transparent-button action-icons ${isDisabled ? 'disabled' : ''}`}
    >
      <i className="edit outline icon" title="Edit" />
    </button>
  );

  if (!resource) return updateButton;

  return (
    <ShouldComponentRender
      resourceType={RESOURCE_TYPE.ENTITY}
      entityAccessType={ENTITY_ACCESS_TYPE.UPDATE}
      resource={resource}
      entity={data}
    >
      {updateButton}
    </ShouldComponentRender>
  );
}

UpdateButtonComponent.propTypes = {
  editItem: PropTypes.func,
  resource: PropTypes.string,
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  rowIndex: PropTypes.number.isRequired,
  disableEditForActive: PropTypes.bool,
  openConfirmModal: PropTypes.func.isRequired,
};

function DuplicateButtonComponent({ duplicateItem, resource, data, rowIndex }) {
  if (!duplicateItem) return null;

  const duplicateButton = (
    <button type="button" onClick={() => duplicateItem(data, rowIndex)} className="transparent-button action-icons">
      <i className="copy outline icon" title="Duplicate" />
    </button>
  );

  if (!resource) return duplicateButton;

  return (
    <ShouldComponentRender
      resourceType={RESOURCE_TYPE.ENTITY}
      entityAccessType={ENTITY_ACCESS_TYPE.CREATE}
      resource={resource}
      entity={data}
    >
      {duplicateButton}
    </ShouldComponentRender>
  );
}

function DeleteButtonComponent({
  deleteItem,
  disableDeleteConfirmationModal,
  openConfirmModal,
  resource,
  data = {},
  rowIndex,
  filterParentTask,
}) {
  if (!deleteItem || (filterParentTask && isTaskParent(data))) return null;
  const isDisabled = data.isActive === true;
  const deleteButton = (
    <button
      type="button"
      className="transparent-button action-icons"
      onClick={() => {
        if (isDisabled) return;
        if (disableDeleteConfirmationModal) {
          deleteItem(data, rowIndex);
        } else {
          openConfirmModal('delete');
        }
      }}
      disabled={isDisabled}
      title={isDisabled ? 'Cannot delete active item' : 'Delete'}
      style={isDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
    >
      <i
        style={isDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
        className="fa fa-trash link-to-views action-icons"
        title="Delete"
      />
    </button>
  );

  if (!resource) return deleteButton;

  return (
    <ShouldComponentRender
      resourceType={RESOURCE_TYPE.ENTITY}
      entityAccessType={ENTITY_ACCESS_TYPE.DELETE}
      resource={resource}
      entity={data}
    >
      {deleteButton}
    </ShouldComponentRender>
  );
}

DuplicateButtonComponent.propTypes = {
  duplicateItem: PropTypes.func,
  resource: PropTypes.string,
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  rowIndex: PropTypes.number.isRequired,
};

DeleteButtonComponent.propTypes = {
  deleteItem: PropTypes.func,
  disableDeleteConfirmationModal: PropTypes.bool,
  openConfirmModal: PropTypes.bool,
  resource: PropTypes.string,
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  rowIndex: PropTypes.number.isRequired,
  filterParentTask: PropTypes.bool,
};

ActionsColumnRenderer.propTypes = {
  context: PropTypes.shape({
    editItem: PropTypes.func,
    deleteItem: PropTypes.func,
    duplicateItem: PropTypes.func,
    disableDeleteConfirmationModal: PropTypes.bool,
    disableDefaultmodal: PropTypes.bool,
    resource: PropTypes.string,
    filterParentTask: PropTypes.bool,
    deleteMessage: PropTypes.string,
    disableEditForActive: PropTypes.bool,
  }).isRequired,
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  rowIndex: PropTypes.number.isRequired,
};
